"use client";

import React, { useCallback, useState, useEffect } from "react";
import { Button, Form, Input, Modal, Typography, Avatar, DatePicker, Popover, message, Select } from "antd";
import { BellOutlined, DownOutlined, UpOutlined } from "@ant-design/icons";
import { createContext } from "@/helpers/createContext";
import { usePrice } from "./PriceContext";
import type { CurrencyIF } from "@/helpers";
import { formatThousands } from "@/helpers";
import logoBlog from "@/asset/images/logo-blog-klarda.png";
import { useCurrencyContext } from "./CurrencyContext";
import useMyMutation from "@/hooks/userMyMutation";
import dayjs from "dayjs";
import { TradingViewChart } from "@/app/components/TradingViewChart";
import { axiosClient } from "@/lib/axiosClient";

interface AlertFormData {
  targetPrice: number;
  comment?: string;
  expiration?: dayjs.Dayjs;
}

interface AlertContextType {
  isAlertModalOpen: boolean;
  selectedSymbol: string | null;
  openAlertModal: (symbol?: string) => void;
  closeAlertModal: () => void;
  onEditAlert: (alert: any) => void;
}

const [Provider, useAlert] = createContext<AlertContextType>();

export const AlertProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isAlertModalOpen, setIsAlertModalOpen] = useState(false);
  const [selectedSymbol, setSelectedSymbol] = useState<string | null>(null);
  const [form] = Form.useForm<AlertFormData>();
  const { getToken, crypto: tokens } = usePrice();
  const { listCurrencies, currentCurrency } = useCurrencyContext();

  const [targetPrice, setTargetPrice] = useState<number>(0);
  const [selectedCurrency, setSelectedCurrency] = useState(currentCurrency);
  const [editingAlert, setEditingAlert] = useState<any>(null);

  const { mutate: createAlert, isPending: isCreating } = useMyMutation(
    {
      url: "/v1/alert",
      method: "post",
    },
    {
      onSuccess() {
        message.success("Alert created successfully");
        closeAlertModal();
        window.dispatchEvent(new Event("refetch-alerts"));
      },
      onError(error) {
        message.error("Failed to create alert: " + error.message);
      },
    },
  );

  const openAlertModal = useCallback((symbol?: string) => {
    setSelectedSymbol(symbol || "BTC");
    setIsAlertModalOpen(true);
    setEditingAlert(null);
  }, []);

  const onEditAlert = useCallback(
    (alert: any) => {
      setEditingAlert(alert);
      setSelectedSymbol(alert.asset);
      setTargetPrice(alert.target);
      form.setFieldsValue({
        targetPrice: alert.target,
        comment: alert.comment,
        expiration: alert.expiration ? dayjs(alert.expiration) : undefined,
      });
      setIsAlertModalOpen(true);
    },
    [form],
  );

  const closeAlertModal = useCallback(() => {
    setIsAlertModalOpen(false);
    setSelectedSymbol(null);
    setEditingAlert(null);
    form.resetFields();
  }, [form]);

  const handleSubmit = async (values: AlertFormData) => {
    const payload = {
      asset: token?.symbol || "",
      price_source: "klarda",
      currency: selectedCurrency.code,
      alert_type: "price_reaches",
      indicator: {},
      target: targetPrice,
      comment: values.comment,
      frequency: "only_once",
      expiration: values.expiration?.toISOString(),
      ...(editingAlert ? { is_active: editingAlert.is_active } : {}),
    };

    try {
      if (editingAlert) {
        await axiosClient.put(`/v1/alert/${editingAlert.id}`, payload);
        message.success("Alert updated successfully");
        closeAlertModal();
        window.dispatchEvent(new Event("refetch-alerts"));
      } else {
        await createAlert(payload);
      }
    } catch (error: any) {
      message.error(error?.response?.data?.message || "Operation failed");
    }
  };

  const token = selectedSymbol ? getToken(selectedSymbol) : null;

  useEffect(() => {
    if (token?.price) {
      setTargetPrice(token.price);
      form.setFieldsValue({
        targetPrice: token.price,
        expiration: dayjs(),
      });
    }
  }, [token, form]);

  const calculatePriceChange = (currentPrice: number, isIncrease: boolean): number => {
    const changeAmount = currentPrice * 0.01;
    return isIncrease ? currentPrice + changeAmount : currentPrice - changeAmount;
  };

  const calculatePriceDistance = (currentPrice: number, targetPrice: number): string => {
    if (!currentPrice) return "0%";
    const difference = ((targetPrice - currentPrice) / currentPrice) * 100;
    const roundedDifference = Math.round(difference);
    return `${roundedDifference}%`;
  };

  const handleIncrease = () => {
    const newValue = calculatePriceChange(targetPrice || 0, true);
    setTargetPrice(newValue);
    form.setFieldValue("targetPrice", newValue);
  };

  const handleDecrease = () => {
    const newValue = calculatePriceChange(targetPrice || 0, false);
    setTargetPrice(newValue);
    form.setFieldValue("targetPrice", newValue);
  };

  const convertPriceToNewCurrency = (price: number, fromCurrency: CurrencyIF, toCurrency: CurrencyIF) => {
    const priceInUSD = fromCurrency.code === "USD" ? price : price / fromCurrency.rate;
    return priceInUSD * toCurrency.rate;
  };

  return (
    <Provider value={{ isAlertModalOpen, selectedSymbol, openAlertModal, closeAlertModal, onEditAlert }}>
      <Modal
        open={isAlertModalOpen}
        onCancel={closeAlertModal}
        footer={null}
        width="95%"
        styles={{
          body: {
            height: "90vh",
            overflow: "auto",
          },
        }}
        style={{
          maxWidth: "1200px",
          margin: "0 auto",
        }}
        className="alert-modal"
      >
        <Form form={form} onFinish={handleSubmit}>
          <div className="p-4">
            {/* Main Content */}
            <div className="flex flex-col lg:flex-row gap-4 lg:gap-8">
              {/* Left Column */}
              <div className="w-full lg:w-[30%] flex flex-col gap-4">
                {/* Asset */}
                <div className="flex flex-col gap-1">
                  <Typography.Text type="secondary">Asset</Typography.Text>
                  <Select
                    size="large"
                    value={selectedSymbol}
                    onChange={value => {
                      setSelectedSymbol(value);
                      const newToken = getToken(value);
                      if (newToken?.price) {
                        setTargetPrice(newToken.price);
                        form.setFieldsValue({
                          targetPrice: newToken.price,
                        });
                      }
                    }}
                    className="w-full mb-1"
                    optionLabelProp="label"
                    showSearch
                    filterOption={(input, option) => {
                      const token = tokens?.find(t => t.symbol === option?.value);
                      return (
                        token?.name.toLowerCase().includes(input.toLowerCase()) ||
                        token?.symbol.toLowerCase().includes(input.toLowerCase()) ||
                        false
                      );
                    }}
                    placeholder="Search for asset"
                  >
                    {tokens?.map(token => (
                      <Select.Option key={token.symbol} value={token.symbol} label={token.name}>
                        <div className="flex items-center gap-2">
                          <Avatar src={token.logo_uri} size={24} className="bg-[#F3BA2F]" />
                          <Typography.Text strong>{token.name}</Typography.Text>
                          <Typography.Text type="secondary">({token.symbol})</Typography.Text>
                        </div>
                      </Select.Option>
                    ))}
                  </Select>
                </div>

                {/* Target Price Section */}
                <div className="flex flex-col gap-4">
                  <div className="flex flex-col gap-1 mt-[3px]">
                    <Typography.Text type="secondary">Target price</Typography.Text>
                    <Form.Item
                      name="targetPrice"
                      rules={[
                        { required: true, message: "Please input target price" },
                        {
                          validator: (_, value) => {
                            if (isNaN(value)) {
                              return Promise.reject("Must be a number");
                            }
                            return Promise.resolve();
                          },
                        },
                      ]}
                      className="m-0"
                    >
                      <Input
                        className="bg-[#F9FAFB] border-[#D1D5DB] p-2 min-h-[42px] rounded-lg"
                        value={targetPrice}
                        onChange={e => {
                          const value = e.target.value.replace(/[^0-9.]/g, "");
                          if (value === "" || !isNaN(parseFloat(value))) {
                            setTargetPrice(parseFloat(value) || 0);
                            form.setFieldValue("targetPrice", parseFloat(value) || 0);
                          }
                        }}
                        prefix={
                          <>
                            <Button
                              size="small"
                              className="p-0"
                              icon={<DownOutlined />}
                              type="default"
                              onClick={handleDecrease}
                            />
                            <span className="ml-1">{selectedCurrency.symbol}</span>
                          </>
                        }
                        suffix={
                          <Button
                            size="small"
                            className="p-0"
                            icon={<UpOutlined />}
                            type="default"
                            onClick={handleIncrease}
                          />
                        }
                      />
                    </Form.Item>
                    <div className="text-gray-500 text-sm">
                      Triggered when price reaches {formatThousands(targetPrice, selectedCurrency)}
                    </div>
                  </div>

                  {/* Current Price Box */}
                  <div className="flex justify-between">
                    <div>
                      <Typography.Text type="secondary">Current price</Typography.Text>
                      <div className="text-xl font-medium mt-1">{formatThousands(token?.price, selectedCurrency)}</div>
                    </div>
                    <div>
                      <Typography.Text type="secondary">Distance to the target price</Typography.Text>
                      <div
                        className={`text-xl font-medium text-right mt-1 ${
                          targetPrice > (token?.price || 0) ? "text-green-500" : "text-red-500"
                        }`}
                      >
                        {calculatePriceDistance(token?.price || 0, targetPrice)}
                      </div>
                    </div>
                  </div>

                  <div className="mt-2">
                    <span className="text-red-500">*</span>
                    <Typography.Text type="secondary" className=" italic ml-1">
                      Updated 6 hours ago
                    </Typography.Text>
                  </div>
                </div>
              </div>

              {/* Right Column */}
              <div className="w-full lg:w-[70%] flex flex-col gap-4">
                {/* Price Source & Currency Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Price source */}
                  <div>
                    <Typography.Text type="secondary">Price source</Typography.Text>
                    <div className="flex items-center gap-2 cursor-pointer">
                      <div className="w-6 h-6 rounded-full bg-[#F3BA2F] flex items-center justify-center">
                        <Avatar src={logoBlog.src} size={16} alt="Klarda Blog" />
                      </div>
                      <span className="text-base">Klarda</span>
                      <DownOutlined />
                    </div>
                  </div>

                  {/* Currency/Pair */}
                  <div>
                    <Typography.Text type="secondary">Currency/Pair</Typography.Text>
                    <div>
                      <Popover
                        trigger="click"
                        placement="bottom"
                        content={
                          <div className="flex flex-col py-2">
                            {listCurrencies.map(currency => (
                              <div
                                key={currency.code}
                                className={`flex items-center gap-2 px-4 py-2 cursor-pointer hover:bg-gray-50 ${
                                  selectedCurrency.code === currency.code ? "bg-gray-100" : ""
                                }`}
                                onClick={() => {
                                  const newPrice = convertPriceToNewCurrency(targetPrice, selectedCurrency, currency);
                                  setSelectedCurrency(currency);
                                  setTargetPrice(newPrice);
                                  form.setFieldValue("targetPrice", newPrice);
                                }}
                              >
                                <Typography.Text type="secondary">
                                  {currency.code} ({currency.symbol})
                                </Typography.Text>
                              </div>
                            ))}
                          </div>
                        }
                      >
                        <div className="flex items-center gap-2 cursor-pointer">
                          <span className="text-base">
                            {selectedCurrency.symbol} ({selectedCurrency.code})
                          </span>
                          <DownOutlined className="text-gray-400" />
                        </div>
                      </Popover>
                      <div className="mt-2 flex gap-2 text-sm">
                        <Typography.Text type="secondary">Current price:</Typography.Text>
                        <span className="font-medium">{formatThousands(token?.price, selectedCurrency)}</span>
                        <Typography.Text type="secondary">Volume 24h:</Typography.Text>
                        <span className="font-medium">{formatThousands(token?.volume, selectedCurrency)}</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Comment & Expiration Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Comment Section */}
                  <div>
                    <Typography.Text type="secondary">Comment</Typography.Text>
                    <Form.Item
                      name="comment"
                      rules={[
                        { required: true, message: "Please input comment" },
                        { max: 500, message: "Comment cannot be longer than 500 characters" },
                      ]}
                      className="m-0"
                    >
                      <Input
                        placeholder="Please input comment"
                        className="!h-[42px] border border-[#D1D5DB] rounded-lg p-2 m-0 bg-[#F9FAFB]"
                      />
                    </Form.Item>
                  </div>

                  {/* Expiration & Message Section */}
                  <div>
                    <Typography.Text type="secondary">Expiration</Typography.Text>
                    <Form.Item
                      name="expiration"
                      rules={[
                        { required: true, message: "Please select expiration time" },
                        {
                          validator: (_, value) => {
                            if (value && value.isBefore(dayjs())) {
                              return Promise.reject("Expiration time cannot be in the past");
                            }
                            return Promise.resolve();
                          },
                        },
                      ]}
                      className="m-0"
                      initialValue={dayjs()}
                    >
                      <DatePicker
                        className="!h-[42px] w-full border border-[#D1D5DB] rounded-lg p-2 bg-[#F9FAFB]"
                        showTime
                        format="YYYY-MM-DD HH:mm:ss"
                        placeholder="Select date and time"
                      />
                    </Form.Item>
                  </div>
                </div>

                {/* Message Section */}
                <div style={{ marginTop: 22 }}>
                  <Typography.Text type="secondary">Message</Typography.Text>
                  <div className="border border-[#D1D5DB] rounded-lg p-2 bg-[#F9FAFB] min-h-[60px]">
                    <Typography.Text type="secondary">
                      {token?.token_symbol} ({token?.symbol}) price reaches{" "}
                      {formatThousands(targetPrice, selectedCurrency)}.
                    </Typography.Text>
                    <br />
                    <Typography.Text type="secondary">Price source by Klarda</Typography.Text>
                  </div>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="flex justify-end mt-4 md:mt-8 mb-4">
              <Button
                type="primary"
                htmlType="submit"
                icon={<BellOutlined />}
                loading={isCreating}
                className="h-10 md:h-12 px-4 md:px-8 rounded-xl bg-[#3861FB] flex items-center gap-2"
              >
                {editingAlert
                  ? isCreating
                    ? "Updating..."
                    : "Update Alert"
                  : isCreating
                  ? "Creating..."
                  : "Create Alert"}
              </Button>
            </div>
            <TradingViewChart height={600} symbol={token?.symbol || ""} />
          </div>
        </Form>
      </Modal>
      {children}
    </Provider>
  );
};

export { useAlert };

